import { useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Tab, Tabs } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useSelectedPageTab } from "app/custom-hooks/useSelectedPageTab";
import { translate } from "app/filters/i18nFilter";
import { isPaidSearchPagesAvailable } from "app/pages/keyword-research/utils/permissions";
import { TabListStyled } from "app/components/keyword-research/landing-pages/styledComponents";
import { WWWNotificationBanner } from "app/pages/keyword-research/website-analysis/pages/topics/components/banners/WWWNotificationBanner";
import { allTrackers } from "app/services/track/track";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TopicsFilters } from "./filters/TopicsFilters";
import { TopicsTableContainer } from "./TopicsTableContainer";
import { buildPageFilterFromKey, isPageFilterMatchingKey } from "./utils";

export const TopicsPageContainer = () => {
    const { params } = useCurrentStateAndParams();
    const { key, pageFilter = "" } = params;
    const currentKey = key as string;
    const currentPageFilter = pageFilter as string;

    const { selectedPageTabIndex, setSelectedPageTabIndex } = useSelectedPageTab(EKeywordTabs);

    useEffect(() => {
        if (!currentKey) {
            return;
        }

        const isFilterMatchingKey = isPageFilterMatchingKey(currentPageFilter, currentKey);

        if (!isFilterMatchingKey) {
            const newPageFilter = buildPageFilterFromKey(currentKey);
            if (newPageFilter) {
                void proNavigator.updateParams({ pageFilter: newPageFilter }, { reload: false });
            }
        }
    }, [currentKey, currentPageFilter]);

    const onPageTabSelect = (index: EKeywordTabs) => {
        if (index !== selectedPageTabIndex) {
            const selectedTabKey = EKeywordTabs[index];
            setSelectedPageTabIndex(index);
            void proNavigator.updateParams({ selectedPageTab: selectedTabKey });
            allTrackers.dispatchTrackEvent("Tab", "click", `Change page tab/${selectedTabKey}`);
        }
    };

    return (
        <Flex
            flexDirection="column"
            width="100%"
            height="100%"
        >
            <WWWNotificationBanner></WWWNotificationBanner>
            <Tabs
                onSelect={onPageTabSelect}
                selectedIndex={selectedPageTabIndex}
            >
                <TabListStyled size={"lg"}>
                    <Tab
                        id={EKeywordTabs.Total}
                        data-automation-item="total"
                    >
                        {translate("search.website.keywords.tab.total")}
                    </Tab>
                    <Tab
                        id={EKeywordTabs.Organic}
                        data-automation-item="organic"
                    >
                        {translate("search.website.keywords.tab.organic")}
                    </Tab>
                    {isPaidSearchPagesAvailable() ? (
                        <Tab
                            id={EKeywordTabs.Paid}
                            data-automation-item="paid"
                        >
                            {translate("search.website.keywords.tab.paid")}
                        </Tab>
                    ) : null}
                </TabListStyled>
                <TopicsFilters />
                <TopicsTableContainer />
            </Tabs>
        </Flex>
    );
};
